/* eslint-disable no-nested-ternary */
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import * as vp from 'vopay';

const { Table } = vp;

type TransactionsOverviewDataProps = {
  valueTab: string;
  periodTab: string;
  transactionsData: {
    Statistics: {
      [StatType: string]: {
        numOfData: {
          yearly: number;
          monthly: number;
          weekly: number;
        };
        volOfData: {
          yearly: number;
          monthly: number;
          weekly: number;
        };
      };
    };
  };
};

type StatusKeyValuePair = {
  display: string;
  value: number;
};

type FC = FunctionComponent<TransactionsOverviewDataProps>;

const TransactionsOverviewTable: FC = (props: TransactionsOverviewDataProps) => {
  const { valueTab, periodTab, transactionsData } = props;
  const { t } = useTranslation('translations', { keyPrefix: 'HomePage.TransactionOverviewWidget' });

  const camelCaseToReadable = (str: string) => {
    const specialCases = new Set(['eft', 'ach', 'rtp', 'oct', 'aft']);
    const lowerCaseStr = str.toLowerCase();

    if (specialCases.has(lowerCaseStr)) {
      return lowerCaseStr.toUpperCase();
    }
    return str.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase());
  };

  // Helper function to get the appropriate value based on valueTab and periodTab
  const getValue = (data: TransactionsOverviewDataProps['transactionsData']['Statistics'][string]) => {
    if (valueTab === 'Number') {
      if (periodTab === 'Yearly') return Number(data.numOfData.yearly || 0);
      if (periodTab === 'Monthly') return Number(data.numOfData.monthly || 0);
      if (periodTab === 'Weekly') return Number(data.numOfData.weekly || 0);
    } else if (valueTab === 'Volume') {
      if (periodTab === 'Yearly') return Number(data.volOfData.yearly || 0);
      if (periodTab === 'Monthly') return Number(data.volOfData.monthly || 0);
      if (periodTab === 'Weekly') return Number(data.volOfData.weekly || 0);
    }
    return 0;
  };

  const AccountsOverviewRows: StatusKeyValuePair[] = Object.keys(transactionsData.Statistics).map((key) => ({
    display: camelCaseToReadable(key),
    value: getValue(transactionsData.Statistics[key]),
  }));

  const tableProps: vp.TableProps = {
    name: 'transactions-overview-table',
    variant: 'overview',
    columns: {
      statusKey: { header: t('Table.statusKey') },
      value: {
        header: <span>{valueTab === 'Volume' ? t('Table.volume') : t('Table.totalAmount')}</span>,
      },
    },
    rows: AccountsOverviewRows.map(({ display, value }) => ({
      statusKey: display,
      value: (
        <span>
          {valueTab === 'Volume'
            ? `${value === undefined ? 0 : value?.toLocaleString()}`
            : `$${value === undefined ? 0 : value?.toLocaleString()}`}
        </span>
      ),
    })),
  };

  return <Table {...tableProps} />;
};

export default TransactionsOverviewTable;
